import { useMemo } from 'react';

import { OptionSelect, type OptionSelectItem } from './OptionSelect';

export interface AuditScoreFieldProps {
  /** Current score. */
  readonly value: number;
  /** Emits the new score picked from the dropdown. */
  readonly onChange: (value: number) => void;
  /** Lowest selectable score (inclusive). */
  readonly min: number;
  /** Highest selectable score (inclusive). */
  readonly max: number;
  /** Accessible name for the numeric select (which carries no visible label
   *  of its own — the visible label lives on the surrounding field). */
  readonly selectAriaLabel: string;
  readonly disabled?: boolean;
  readonly dataTestId?: string;
}

const SCORE_STEP = 1;

/**
 * Audit-score picker: a numeric `OptionSelect` (min–max). Previously paired
 * with a Stencil `Slider`, but the slider added no value over the dropdown for
 * a small integer range and was dropped per design — the dropdown is the sole
 * control now. Controlled: the owner holds the score and updates it on change.
 */
export function AuditScoreField({
  value,
  onChange,
  min,
  max,
  selectAriaLabel,
  disabled = false,
  dataTestId,
}: AuditScoreFieldProps): JSX.Element {
  // Numeric options min..max, memoised so the array identity is stable across
  // renders (OptionSelect compares options by reference).
  const scoreOptions = useMemo<readonly OptionSelectItem[]>(() => {
    const options: OptionSelectItem[] = [];
    for (let score = min; score <= max; score += SCORE_STEP) {
      options.push({ value: String(score), label: String(score) });
    }
    return options;
  }, [min, max]);

  const handleSelectChange = (next: string): void => {
    const parsed = Number(next);
    if (!Number.isNaN(parsed)) onChange(parsed);
  };

  return (
    <OptionSelect
      options={scoreOptions}
      value={String(value)}
      onChange={handleSelectChange}
      disabled={disabled}
      ariaLabel={selectAriaLabel}
      {...(dataTestId ? { dataTestId } : {})}
    />
  );
}
